/**
 * @module cercaliagl/LonLat
 */

import maplibregl from 'maplibre-gl';
import { transform } from 'ol/proj';
import BaseObject from './BaseObject';
import Proj from './proj';

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const lonLat = new cercaliagl.LonLat(1.4151455, 41.24514);
 * ```
 * <br/>
 *
 * Coordinates. Let you specify the coordinates in EPSG:4326 projection<br/>
 * More information about this projection: https://epsg.io/4326
 *
 * @api
 */
class LonLat extends BaseObject {
  /**
   * @constructor
   * @param {number|string} lon Longitude, in geographic coordinate system
   * @param {number|string} lat Latitude, in geographic coordinate system
   */
  constructor(lon, lat, srs = Proj.EPSG4326) {
    super();
    const transformedCoords = srs === Proj.EPSG3857 ? transform([Number(lon), Number(lat)], Proj.EPSG3857, Proj.EPSG4326) : [Number(lon), Number(lat)];
    /**
     * @private
     * @type {number}
     */
    // eslint-disable-next-line prefer-destructuring
    this.lon_ = transformedCoords[0];

    /**
     * @private
     * @type {number}
     */
    // eslint-disable-next-line prefer-destructuring
    this.lat_ = transformedCoords[1];
  }

  /**
   * @param {import('maplibre-gl/src/geo/lng_lat').default} lonLat Lon lat
   */
  static fromMapboxgl(lonLat) {
    return new LonLat(lonLat.lng, lonLat.lat);
  }

  /**
   * @param {import("./util").GeometryGeoJSON} [geometry] GeoJSON Geometry. Use that or `wkt` property.
   */
  static fromGeometry(geometry) {
    return new LonLat(geometry.coordinates[0], geometry.coordinates[1]);
  }

  /**
   * Obtain longitude coordinate
   * @return {number} Coordinate Longitude
   * @api
   */
  getLon() {
    return this.lon_;
  }

  /**
   * Obtain latitude coordinate
   * @return {number} Latitud
   * @api
   */
  getLat() {
    return this.lat_;
  }

  /**
   * @return {LngLat}
   */
  toMapboxLngLat() {
    return new maplibregl.LngLat(this.lon_, this.lat_);
  }

  /**
   * Position to string. Format `longitude,latitude`.
   * @return {string} To string longitude,latitude. For example: `"1.41651,41.541451"
   * @override
   * @api
   */
  toString() {
    return `${this.lon_},${this.lat_}`;
  }

  /**
   * Transform position to array `[longitude, latitude]`.
   * @api
   * @return {Array.<number>}
   */
  toArray() {
    return [this.lon_, this.lat_];
  }
}

export default LonLat;
